<script setup lang="ts">
import { ref } from 'vue'
import { notificationButtonApi } from '../services/notificationButtonApi'
import type { INotificationButton } from '../types/notification.types'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

/**
 * Рендер action-кнопок в карточке уведомления.
 *
 * Поведение по type:
 * - `url` — открыть ссылку в новой вкладке (через window.open).
 * - `button` — POST на url с телом payload (через notificationButtonApi.invokeButton).
 *   При успешном ответе и delete_after_click=true — кнопка скрывается из локального списка.
 *
 * На время отправки кнопка показывает спиннер и блокируется. При ошибке POST — toast через
 * глобальный feedbackStore.
 */
const props = defineProps<{
  buttons: INotificationButton[]
}>()

const feedback = useFeedbackStore()

// Локальный список — чтобы скрывать кнопки с delete_after_click без перезагрузки props
const localButtons = ref<INotificationButton[]>([...props.buttons])
const loadingIndex = ref<number | null>(null)

function colorOf(button: INotificationButton): string {
  return button.color ?? 'primary'
}

/* VBtn variant="tonal" в нашей теме рендерится без backgroundColor — задаём вручную. */
function styleOf(button: INotificationButton): Record<string, string> {
  return {
    backgroundColor: `rgba(var(--v-theme-${colorOf(button)}), 0.16)`,
  }
}

async function onClick(button: INotificationButton, index: number): Promise<void> {
  if (loadingIndex.value !== null) return

  if (button.type === 'url') {
    window.open(button.url, '_blank', 'noopener,noreferrer')
    if (button.delete_after_click) {
      localButtons.value.splice(index, 1)
    }
    return
  }

  // type === 'button'
  loadingIndex.value = index
  try {
    await notificationButtonApi.invokeButton(button.url, button.payload)
    if (button.delete_after_click) {
      localButtons.value.splice(index, 1)
    }
  } catch (error) {
    feedback.showToast({
      type: 'error',
      message: 'Не удалось выполнить действие. Попробуйте ещё раз.',
    })
    console.error('[NotificationActions] invokeButton failed', error)
  } finally {
    loadingIndex.value = null
  }
}
</script>

<template>
  <div
    v-if="localButtons.length"
    class="notification-actions d-flex"
    @click.stop
  >
    <VBtn
      v-for="(button, index) in localButtons"
      :key="`${button.type}:${button.url}:${index}`"
      :color="colorOf(button)"
      variant="tonal"
      size="small"
      rounded="sm"
      class="notification-actions__btn text-none"
      :style="styleOf(button)"
      :loading="loadingIndex === index"
      :disabled="loadingIndex !== null && loadingIndex !== index"
      @click="onClick(button, index)"
    >
      {{ button.text }}
    </VBtn>
  </div>
</template>

<style lang="scss" scoped>
.notification-actions {
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 6px;
}

.notification-actions__btn {
  flex: 0 0 auto;
  padding-block: 8px;
  block-size: auto;
}
</style>
